import { useState, type ReactNode, type UIEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import { WELLBEING_PATHS } from './wellbeingPaths';

interface WellbeingRoomProps {
  title: string;
  description: string;
  /** Right of the title: a setting, a status. */
  actions?: ReactNode;
  /**
   * While an exercise runs: the header steps back so the tool has the
   * screen. Still there and still usable, just quiet.
   */
  dimmed?: boolean;
  /**
   * Fill the window on desktop: a one-line header, and the tool takes every
   * pixel below it, so the page never scrolls (Focus). Phones still scroll.
   */
  fill?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Below `sm` the header is a bar that stays at the top of the page's scroll:
 * "Back" and the room's name in one row. A tool page is entered from the hub
 * and left by Back, so the way out must not scroll away; the description
 * (read once, on arrival) does. The hub, a top-level page like Home, keeps
 * its title in the flow.
 *
 * Transparent at rest, so the bar is just the page's header sitting on the
 * page's ground and grid, like every other page's. It takes a backdrop only
 * once something has scrolled under it: a see-through pinned bar would draw
 * "Back" and the title over the text passing beneath them.
 */
const phoneBar = (scrolled: boolean) =>
  cn(
    'max-sm:sticky max-sm:top-0 max-sm:z-20 max-sm:-mx-4 max-sm:border-b max-sm:border-transparent max-sm:px-4 max-sm:py-1.5 max-sm:transition-colors max-sm:duration-fast',
    scrolled &&
      'max-sm:border-border/60 max-sm:bg-background/90 max-sm:backdrop-blur-sm'
  );

/**
 * Stepping back while an exercise runs is for wider screens: on a phone the
 * header is the pinned bar, and a see-through bar over moving content reads
 * as a glitch, not as calm.
 */
const DIM = (amount: 'some' | 'more') =>
  cn(
    amount === 'some' ? 'sm:opacity-75' : 'sm:opacity-40',
    'focus-within:opacity-100 hover:opacity-100'
  );

/**
 * The frame for one Wellbeing tool: a way back to the hub, a title, and the
 * tool. Same width and gutters as every other page, so moving from the hub
 * into a tool does not shift the page. The page owns its scroll: AppLayout's
 * `main` is overflow-hidden.
 */
export function WellbeingRoom({
  title,
  description,
  actions,
  dimmed = false,
  fill = false,
  children,
  className,
}: WellbeingRoomProps) {
  const [scrolled, setScrolled] = useState(false);
  const onScroll = (e: UIEvent<HTMLDivElement>) =>
    setScrolled(e.currentTarget.scrollTop > 0);
  if (fill) {
    return (
      <div
        className='h-full overflow-y-auto lg:overflow-hidden'
        onScroll={onScroll}
      >
        <div className='relative isolate min-h-full lg:h-full'>
          <div
            className={cn(
              'mx-auto flex w-full max-w-7xl flex-col px-4 pb-24 pt-4 max-sm:pt-0 sm:px-6 lg:h-full lg:px-8 lg:pb-5',
              className
            )}
          >
            <header
              className={cn(
                'mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 transition-opacity duration-slow max-sm:mb-2 max-sm:flex-nowrap max-sm:gap-x-3',
                phoneBar(scrolled),
                dimmed && DIM('some')
              )}
            >
              <BackLink dimmed={dimmed} />
              <h1 className='truncate text-2xl font-bold text-foreground max-sm:text-lg max-sm:font-semibold'>
                {title}
              </h1>
              <p
                className={cn(
                  'hidden truncate text-muted-foreground',
                  // While a round runs the subtitle goes, rather than fading below legible contrast.
                  !dimmed && 'md:block'
                )}
              >
                {description}
              </p>
              {actions && <div className='ml-auto shrink-0'>{actions}</div>}
            </header>
            <div className='min-h-0 lg:flex-1'>{children}</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className='h-full overflow-y-auto' onScroll={onScroll}>
      {/* `relative isolate` lifts the content above the app's grid backdrop
        (a fixed `z-0` layer that paints over anything unpositioned). */}
      <div className='relative isolate min-h-full'>
        <div
          className={cn(
            'mx-auto w-full max-w-7xl px-4 pb-10 pt-8 max-sm:pt-0 sm:px-6 lg:px-8 [@media(max-height:860px)]:pb-4',
            className
          )}
        >
          <header
            className={cn(
              'flex items-start justify-between gap-4 transition-opacity duration-slow max-sm:items-center',
              phoneBar(scrolled),
              dimmed && DIM('more')
            )}
          >
            {/* Back on its own row beside `sm`, so the title keeps the same
              left edge as every other page's; one row in the phone bar. */}
            <div className='min-w-0 max-sm:flex max-sm:items-center max-sm:gap-3'>
              <BackLink dimmed={dimmed} className='mb-4 max-sm:mb-0' />
              <h1 className='truncate text-3xl font-bold text-foreground max-sm:text-lg max-sm:font-semibold'>
                {title}
              </h1>
            </div>
            {actions && <div className='shrink-0 self-end'>{actions}</div>}
          </header>
          {/* Outside the header so it scrolls away under the phone bar. */}
          <p
            className={cn(
              'mb-8 mt-1 text-muted-foreground transition-opacity duration-slow max-sm:mb-6 max-sm:mt-3',
              dimmed && DIM('more')
            )}
          >
            {description}
          </p>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * "Back", read as "Back to Wellbeing" apart from the Navbar's own link.
 *
 * A quiet ghost button - muted text and an arrow, no border or shadow - the
 * same as the Back in Mindspace's tools (this section's sibling: a hub with a
 * page per tool) and Settings' "‹ Settings". It used to be a bordered,
 * shadowed pill, the only one in the app. `-ml-2` puts the arrow, not the
 * hover fill, on the page's left edge.
 */
function BackLink({
  dimmed,
  className,
}: {
  dimmed: boolean;
  className?: string;
}) {
  return (
    <Link
      to={WELLBEING_PATHS.hub}
      className={cn(
        '-ml-2 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-muted-foreground transition-colors duration-fast hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:h-11',
        // While the header steps back (`DIM`), muted text at 75% opacity is
        // 3.4:1 on the page ground - under AA. Full-strength text dimmed reads
        // about as quiet and stays legible.
        dimmed && 'sm:text-foreground',
        className
      )}
    >
      <ArrowLeft className='h-4 w-4' aria-hidden='true' />
      Back<span className='sr-only'> to Wellbeing</span>
    </Link>
  );
}
